import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import PrimaryButton from 'util/PrimaryButton';
import PreviewingReservationService from 'api/PreviewingReservationService';
import { UpsertCustomerDto } from 'api/generated/openapi';
import LoadingSpinner from 'util/LoadingSpinner';

const PreviewingReservationQuestionnaireComponent: React.FC = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [isContractor, setIsContractor] = useState<boolean | null>(null);
  const [budget, setBudget] = useState<string>('');
  const [job, setJob] = useState<string>('');
  const [jobOther, setJobOther] = useState<string>('');
  const [isPreferredProperty, setIsPreferredProperty] = useState<boolean | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const initialValues = useRef({
    isContractor: null as boolean | null,
    budget: '',
    job: '',
    jobOther: '',
    isPreferredProperty: null as boolean | null,
  });
  const [isSending, setIsSending] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        if (!reservationId) return;
        const reservationDetail = await PreviewingReservationService.getPreviewingReservation({
          reservationId,
        });

        if (reservationDetail.reservationCancelled === 1) {
          setIsSubmitted(true);
          return;
        }

        const c = (reservationDetail.customer as UpsertCustomerDto | null) ?? null;

        const vals = {
          isContractor: c?.isContractor ?? null,
          budget: c?.budget ?? '',
          job: c?.job ?? '',
          jobOther: c?.jobOther ?? '',
          isPreferredProperty: c?.isPreferredProperty ?? null,
        };
        initialValues.current = vals;
        setIsContractor(vals.isContractor);
        setBudget(vals.budget);
        setJob(vals.job);
        setJobOther(vals.jobOther);
        setIsPreferredProperty(vals.isPreferredProperty);
      } catch (e) {
        // customer が無くてもアンケートは表示・送信できるため、ここでは画面遷移しない
      } finally {
        setIsLoading(false);
      }
    };
    fetchCustomer();
  }, [reservationId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      if (isSubmitted) return;
      if (!reservationId) {
        setErrorMessage('予約IDが取得できませんでした。時間をおいて再度お試しください。');
        return;
      }
      setIsSending(true);
      setErrorMessage('');

      const params: UpsertCustomerDto = {
        isContractor,
        budget: budget === '' ? null : budget,
        job: job === '' ? null : job,
        jobOther: job === 'その他' ? (jobOther === '' ? null : jobOther) : null,
        isPreferredProperty,
      };

      await PreviewingReservationService.createCustomer(reservationId, params);

      setIsSubmitted(true);
    } catch (e) {
      setErrorMessage('アンケートの送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSending(false);
    }
  };

  const handleBackToDetail = () => {
    if (reservationId) {
      navigate(`/previewing-reservation-detail/${reservationId}`);
    } else {
      navigate('/');
    }
  };

  const showJobOtherInput = job === 'その他';

  const hasChanged =
    isContractor !== initialValues.current.isContractor ||
    budget !== initialValues.current.budget ||
    job !== initialValues.current.job ||
    jobOther !== initialValues.current.jobOther ||
    isPreferredProperty !== initialValues.current.isPreferredProperty;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col xs={12} md={9} className="py-3">
          {isSubmitted ? (
            <div>
              <p className="mb-4">アンケートへのご協力ありがとうございました。</p>
              <PrimaryButton
                label="予約詳細画面に戻る"
                type="button"
                className="w-100"
                onClick={handleBackToDetail}
              />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="h5 mb-3">事前アンケート</h2>
              <p className="mb-4">
                任意のアンケートです。差し支えなければご回答をお願いいたします。
              </p>
              {errorMessage && <p className="text-danger mb-3">{errorMessage}</p>}
              <div className="mb-3">
                <label className="form-label d-block">予約者は契約者ご本人ですか？（任意）</label>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="contractor-self"
                      name="contractorType"
                      value="self"
                      checked={isContractor === true}
                      onChange={() => setIsContractor(true)}
                    />
                    <label className="form-check-label" htmlFor="contractor-self">
                      本人
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="contractor-not-self"
                      name="contractorType"
                      value="notSelf"
                      checked={isContractor === false}
                      onChange={() => setIsContractor(false)}
                    />
                    <label className="form-check-label" htmlFor="contractor-not-self">
                      本人ではない
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="budget" className="form-label">
                  ご予算（任意）
                </label>
                <div className="d-flex align-items-center">
                  <input
                    id="budget"
                    type="number"
                    min={0}
                    className="form-control"
                    placeholder="50000"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                  />
                  <span className="ms-2">円</span>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="job" className="form-label">
                  職業（任意）
                </label>
                <select
                  id="job"
                  className="form-select"
                  value={job}
                  onChange={(e) => {
                    const nextJob = e.target.value;
                    setJob(nextJob);
                    if (nextJob !== 'その他') {
                      setJobOther('');
                    }
                  }}
                >
                  <option value="">選択してください</option>
                  <option value="会社員">会社員</option>
                  <option value="自営業">自営業</option>
                  <option value="学生">学生</option>
                  <option value="パート・アルバイト">パート・アルバイト</option>
                  <option value="無職">無職</option>
                  <option value="その他">その他</option>
                </select>
                {showJobOtherInput && (
                  <input
                    type="text"
                    className="form-control mt-2"
                    placeholder="その他の職業を入力してください"
                    value={jobOther}
                    onChange={(e) => setJobOther(e.target.value)}
                  />
                )}
              </div>

              <div className="mb-4">
                <label className="form-label d-block">希望物件の有無（任意）</label>
                <div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="visit-only"
                      name="preferredProperty"
                      value="visitOnly"
                      checked={isPreferredProperty === false}
                      onChange={() => setIsPreferredProperty(false)}
                    />
                    <label className="form-check-label" htmlFor="visit-only">
                      来店のみ
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="has-preferred"
                      name="preferredProperty"
                      value="hasPreferred"
                      checked={isPreferredProperty === true}
                      onChange={() => setIsPreferredProperty(true)}
                    />
                    <label className="form-check-label" htmlFor="has-preferred">
                      希望物件あり
                    </label>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-md-row gap-2">
                <PrimaryButton
                  label={isSending ? '送信中...' : 'アンケートを送信する'}
                  type="submit"
                  className="flex-fill"
                  disabled={isSending || !reservationId || !hasChanged}
                />
                <PrimaryButton
                  label="予約詳細画面に戻る"
                  type="button"
                  className="flex-fill"
                  onClick={handleBackToDetail}
                  disabled={isSending}
                />
              </div>
            </form>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default PreviewingReservationQuestionnaireComponent;
